import { useMemo, type ReactNode } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useEffect } from 'react';
import { App as AntApp, ConfigProvider } from 'antd';

import { antdThemeFor } from 'app/theme/adapters/antd';
import ConnectivityBanner from 'app/components/common/ConnectivityBanner';
import ErrorBoundary from 'app/components/common/ErrorBoundary';
import { startReleaseSync } from 'app/platform/release/release';
import { router } from 'router';
import {
  DeviceProvider,
  useDevice,
} from 'app/hooks/compactTouchUI/DeviceContext';

function DeviceGate({ children }: { children: ReactNode }) {
  const { isDeviceReady } = useDevice();
  if (!isDeviceReady) return null;
  return <>{children}</>;
}

// Above both shells so public routes are themed too, and so `App.useApp()`
// resolves everywhere rather than only inside ProtectedShell.
function AntdProvider({ children }: { children: ReactNode }) {
  const { isMobileUI } = useDevice();
  // A fresh theme object makes ConfigProvider re-derive all ~500 tokens.
  const theme = useMemo(() => antdThemeFor(isMobileUI), [isMobileUI]);

  return (
    <ConfigProvider theme={theme} modal={{ centered: isMobileUI }}>
      <AntApp component={false}>{children}</AntApp>
    </ConfigProvider>
  );
}

function App() {
  // TODO: add eslint config to catch these while commiting
  if (import.meta.env.VITE_STAGE !== 'true') {
    console.log = () => {};
  }

  useEffect(() => {
    // Kept it here as there are public routes sent to the client which might be using this font
    if (import.meta.env.VITE_CUSTOM_FONTS_ENABLED === 'true') {
      document.body.classList.add('custom-fonts-enabled');
    }
  }, []);

  // Finds new builds in the background; useReleaseSwitch moves to one at the next page change.
  useEffect(() => startReleaseSync(), []);

  return (
    <ErrorBoundary>
      <DeviceProvider>
        <DeviceGate>
          <AntdProvider>
            {/* Outside the router so a route error can't unmount them. */}
            <ConnectivityBanner />
            <RouterProvider router={router} />
          </AntdProvider>
        </DeviceGate>
      </DeviceProvider>
    </ErrorBoundary>
  );
}

export default App;
